import debug from 'debug'
import { datadogRum } from '@datadog/browser-rum'

import type { ErrorsObject, ServerErrorResponse } from 'types/server-error'

/**
 * Logs error to console with debug() and captures it to Sentry/Datadog.
 *
 * Provides centralized error handling with development logging and production
 * error tracking. Automatically formats errors for debugging and monitoring.
 *
 * @param error - The caught error to log and capture
 * @param options - Configuration options for error handling
 * @param options.moduleName - Module where error was caught (e.g., 'ExplorerActions')
 * @param options.description - Custom description for logs (e.g., 'Unexpected pane.type: undefined')
 * @param options.extra - Additional context data for error tracking
 * @param options.tags - Tags for categorizing the error
 *
 * @example
 * ```typescript
 * try {
 *   riskyOperation()
 * } catch (error) {
 *   captureError(error, {
 *     moduleName: 'TrackUpload',
 *     description: 'Failed to upload track',
 *     extra: { trackId: 123, fileSize: 1024 },
 *     tags: { audioType: 'mp3' }
 *   })
 * }
 * ```
 */
export default function captureError(
  error: Error,
  options: {
    moduleName?: string
    description?: string
    tags?: any
    extra?: any
  } = {},
) {
  const { description, moduleName, tags = {}, extra = {} } = options
  const debugFunc = debug(moduleName || tags.module || 'unknown')
  // checking if debug is enabled to prevent expensive access to error.stack in production
  if (debugFunc.enabled) {
    debugFunc(description || extra || 'Error', error ? error.stack : error)
  }
  if (process.env.NODE_ENV === 'production') {
    datadogRum.addError(error, {
      module: moduleName,
      description,
      ...tags,
      ...extra,
    })
  }
}

/**
 * Converts HTML5 media error codes to human-readable status strings.
 *
 * Maps numeric media error codes to their corresponding constant names
 * for better debugging and error reporting.
 *
 * @param code - HTML5 media error code (1-4)
 * @returns Human-readable error status string
 *
 * @example
 * ```typescript
 * const errorStatus = getMediaErrorStatus(3) // 'MEDIA_ERR_DECODE'
 * const unknownStatus = getMediaErrorStatus(99) // 'MEDIA_ERR_UNKNOWN'
 * ```
 */
export function getMediaErrorStatus(code: number): string {
  switch (code) {
    case 1:
      return 'MEDIA_ERR_ABORTED'
    case 2:
      return 'MEDIA_ERR_NETWORK'
    case 3:
      return 'MEDIA_ERR_DECODE'
    case 4:
      return 'MEDIA_ERR_SRC_NOT_SUPPORTED'
    default:
      return 'MEDIA_ERR_UNKNOWN'
  }
}

/**
 * Checks if an error is a connection-related TypeError.
 *
 * Identifies errors that occur when trying to read properties of
 * undefined objects, typically indicating connection issues.
 *
 * @param error - Error object to check
 * @returns True if the error appears to be connection-related
 *
 * @example
 * ```typescript
 * const error = new TypeError("Cannot read property 'data' of undefined")
 * const isConnection = checkIfConnectError(error) // true
 *
 * const otherError = new Error('Something else')
 * const isNotConnection = checkIfConnectError(otherError) // false
 * ```
 */
export function checkIfConnectError(error: Error): boolean {
  const isConnectError: boolean =
    error.name === 'TypeError' && error.message
      ? // V8 changed the wording from "Cannot read property 'x' of ..." to
        // "Cannot read properties of ... (reading 'x')" - match both.
        error.message.indexOf('Cannot read property') === 0 ||
        error.message.indexOf('Cannot read properties') === 0
      : false
  return isConnectError
}

const serverErrorMapping: {
  [key: string]: string
} = {
  'Sum of splits cannot be higher than 100%':
    "Sorry! The sum of the splits can't be higher than 100%",
}

/**
 * Recursively extracts the first error message from a nested error object.
 *
 * Traverses error object structure to find the first meaningful error message,
 * applying any custom error mappings for user-friendly messages.
 *
 * @param errorObject - Nested error object from server response
 * @returns First error message found, or null if none
 *
 * @example
 * ```typescript
 * const errors = {
 *   field1: ['Required field'],
 *   field2: { nested: ['Invalid value'] }
 * }
 * const message = getError(errors) // 'Required field'
 * ```
 */
const getError = (errorObject: ErrorsObject): string | null | undefined => {
  const errorValues = Object.values(errorObject)

  const val = errorValues[0]
  if (!val) {
    return null
  } else if (typeof val === 'object') {
    const nestedError = val as ErrorsObject
    // rare case (serializer in serializer)
    return getError(nestedError)
  } else if (typeof val === 'string') {
    return serverErrorMapping[val] || val
  } else {
    return null
  }
}

/**
 * Parses server error responses into user-friendly error messages.
 *
 * Handles various server error response formats and extracts meaningful
 * error messages with fallback to default message.
 *
 * @param response - Server error response in various formats
 * @param options - Configuration options
 * @param options.fallback - Default message if no error can be parsed
 * @returns User-friendly error message
 *
 * @example
 * ```typescript
 * // String response
 * const error1 = parseServerError('Invalid email format')
 * // error1 = 'Invalid email format'
 *
 * // Array response
 * const serverResponse = [{ errors: { email: ['Required field'] } }]
 * const error2 = parseServerError(serverResponse)
 * // error2 = 'Required field'
 *
 * // Fallback
 * const error3 = parseServerError(null, { fallback: 'Custom error' })
 * // error3 = 'Custom error'
 * ```
 */
export const parseServerError = (
  response?: string | ServerErrorResponse | ErrorsObject | null,
  options: {
    fallback?: string
  } = {},
): string => {
  const { fallback = 'Sorry, an error occurred' } = options

  if (response) {
    if (typeof response === 'string') return response

    let result: string | null | undefined = null
    if (response && Array.isArray(response) && response.length > 0) {
      const errorObject = response[0]
      if (errorObject) {
        result = getError(errorObject.errors)
      }
    } else if (typeof response === 'object' && !Array.isArray(response)) {
      result = getError(response)
    }
    if (result) return result
  }

  return fallback
}
